import {
  useState, useMemo, useContext,
} from 'react';
import {
  string, object, bool, func, number, array,
} from 'prop-types';
import {
  Button, Modal, Select,
} from 'antd';

import { i18nContext } from 'context/i18n';
import { getIsMobile } from 'utils/helpers';
import { CamperThumbnailSVG } from 'components/iconsSVG/camperThumbnail';
import { BurgerMenuSVG } from 'components/iconsSVG/burgerMenu';
import { MapPinSVG } from 'components/iconsSVG/mapPin';
import { NnormalSVG } from 'components/iconsSVG/nnormal';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import Map from 'components/map';

import { IS_CAMPER } from 'utils/constants/system';

import styles from './style.module.css';

const Logo = IS_CAMPER ? CamperThumbnailSVG : NnormalSVG;

const StoreItem = ({
  address1, cityName, name, postalCode, telephone, latitude, longitude, storeHours,
  handleViewStoreInMap, handleSelectStore,
}) => {
  const { t } = useContext(i18nContext);

  return (
    <div className={styles.pickupStore} onClick={handleSelectStore}>
      <div>
        <Logo />
      </div>
      <div className={styles.dataColumn}>
        <p className={styles.columnTitle}>{name}</p>

        <div className={styles.addressWrapper}>
          <p>
            {address1}
            <br />
            {postalCode}, {cityName}
            <br />
            {t('generico', 'telephone', 'Telephone')}: <a onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); }} className={styles.phoneLink} href={`tel:${telephone}`}>{telephone}</a>
          </p>
          <span onClick={(ev) => {
            handleViewStoreInMap(latitude, longitude);
            ev.preventDefault();
            ev.stopPropagation();
          }} className={`${styles.viewInMap} ${styles.clickable}`}>{t('tiendas.locator', 'view.in.map', 'View in map')}</span>
        </div>

      </div>

      <div className={styles.scheduleColumn}>
        <p className={styles.openingHours}>{t('ficha.producto', 'opening.hours', 'OPENING HOURS')}</p>
        <ul className={styles.schedule}>
          {
            storeHours.map((item, index) => <li key={`sch-${name}-${index}`} className={styles.schedulePairWrapper}>
              <span>{item.days}</span>
              <span>{item.timetable}</span>
            </li>)
          }
        </ul>
      </div>
    </div>
  );
};

StoreItem.propTypes = {
  address1: string,
  cityName: string,
  name: string,
  postalCode: string,
  telephone: string,
  latitude: number,
  longitude: number,
  storeHours: array,
  sizes: array,
  selectedSize: string,
  handleViewStoreInMap: func,
  openAppointmentModal: func,
  whatsappPhone: string,
  handleSelectStore: func,
};

const PickUpInStoreModal = ({
  visible, onClose, shipment, handleSelectStore,
}) => {
  const { t } = useContext(i18nContext);
  const isMobile = getIsMobile('1439.5px');
  const [ filteredCity, setFilteredCity ] = useState(null);
  const [ centeredStore, setCenteredStore ] = useState(null);
  const [ showingMap, setShowingMap ] = useState(true);

  const handleViewStoreInMap = (latitude, longitude) => {
    setCenteredStore([ latitude, longitude ]);
    setShowingMap(true);
  };

  const filterStores = () => {
    if (!shipment?.stores) return [];
    if (filteredCity === null) return shipment.stores;
    return shipment.stores.filter((item) => item.cityName === filteredCity);
  };

  const MapComponentMemo = useMemo(() => <Map
    sticky={false}
    visible={showingMap}
    center={centeredStore}
    viewStoreInMap={false}
    zoom={filteredCity === null ? 3 : 12}
    pins={filterStores().map((item) => ({ latitude: item.latitude, longitude: item.longitude }))}
    onClickMarker={(coords) => {
      const [ storeLat, storeLon ] = coords;
      const storeByCoords = filterStores().filter((item) => item.latitude === storeLat && item.longitude === storeLon)[0];
      if (storeByCoords !== undefined) {
        handleSelectStore(storeByCoords);
      }
    }}
    fitToBounds
  />, [ isMobile, showingMap, filteredCity ]);

  const handleSelectCity = (value) => {
    setCenteredStore(null);

    if (value === false) {
      setFilteredCity(null);
    } else {
      setFilteredCity(value);
    }
  };

  if (shipment === undefined) { // no debería ni poder abrirse ya que no hay un shipping con id 99
    return null;
  }

  return (
    <Modal
      className={`${styles.pickupModal} ${visible ? styles.mapModal : ''}`}
      width={'800px'}
      visible={visible}
      centered={true}
      closable={true}
      destroyOnClose={true}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseModalSVG/>}
    >
      <div className={styles.dispoStoresContainer}>
        <h1 className={styles.pickupTitle}>{t('tiendas.locator', 'in.store', 'In store')}</h1>
        <div className={styles.pickupHeader}>
          <div className={styles.selectWrapper}>
            <span>{t('tiendas.locator', 'please.select.city', 'Please, select a city')}:</span>
            <Select
              className={styles.citySelector}
              placeholder={t('tiendas.locator', 'select.city', 'Select a city')}
              onChange={handleSelectCity}
              suffixIcon={<ChevronSVG/>}
            >
              <Select.Option value={false}>
                -- {t('generico', 'city.list', 'City list')} --
              </Select.Option>
              {
                shipment?.cities.map((item, index) => <Select.Option key={`city-option-${index}`} value={item}>{item}</Select.Option> ?? [])
              }
            </Select>
          </div>
          <div className={styles.dispoTabsContainer}>
            <Button onClick={() => setShowingMap(false)} autoFocus={true} className={`${styles.dispoTab} ${showingMap ? '' : styles.active}`} icon={<BurgerMenuSVG />}>{t('generico', 'view.list', 'View List')}</Button>
            <Button onClick={() => setShowingMap(true)} className={`${styles.dispoTab} ${showingMap ? styles.active : ''}`} icon={<MapPinSVG />}></Button>
          </div>
        </div>

        <div className={showingMap ? styles.mapContainer : 'invisible'}>
          {MapComponentMemo}
        </div>
        <div className={`${styles.storesList} ${showingMap ? 'invisible' : ''}`}>
          {
            filterStores().map((item) => <StoreItem key={item.id} {...item} handleViewStoreInMap={handleViewStoreInMap} handleSelectStore={() => handleSelectStore(item)} />)
          }
        </div>
      </div>
      { showingMap ?
        <Button type="primary" className={styles.changeTabBtn} block onClick={() => setShowingMap(false)}>{t('generico', 'view.list', 'View List')}</Button> :
        <Button type="primary" className={styles.changeTabBtn} block onClick={() => setShowingMap(true)}>{t('generico', 'view.map', 'View Map')}</Button>
      }
    </Modal>
  );
};

PickUpInStoreModal.propTypes = {
  productSheet: object,
  shipment: object,
  visible: bool,
  onClose: func,
  handleSelectStore: func,
};

PickUpInStoreModal.defaultProps = {
  productSheet: {},
  visible: false,
  onClose: () => true,
};

export default PickUpInStoreModal;
